export type FreshnessTone = 'fresh' | 'stale' | 'offline';

export type DataFreshnessBadgeProps = {
  at: Date;
  tone: FreshnessTone;
  now?: Date; // overridable for stories / tests
};

// `*-fg` companion tokens (introduced alongside this component refactor)
// pair with the matching `*-bg` to clear WCAG AA at 11px without the
// leading-dot workaround.
const TONE: Record<FreshnessTone, { bg: string; fg: string }> = {
  fresh: { bg: 'bg-healthy-bg', fg: 'text-healthy-fg' },
  stale: { bg: 'bg-warn-bg', fg: 'text-warn-fg' },
  offline: { bg: 'bg-critical-bg', fg: 'text-critical' },
};

const TONE_LABEL: Record<FreshnessTone, string> = {
  fresh: 'up to date',
  stale: 'stale',
  offline: 'offline',
};

function relativeTime(at: Date, now: Date): string {
  const diffMs = now.getTime() - at.getTime();
  const absSec = Math.max(0, Math.floor(Math.abs(diffMs) / 1000));
  if (absSec < 60) return 'just now';
  const min = Math.floor(absSec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

/**
 * "updated 2m ago" pill. The tone is computed by the data layer based on
 * the source's freshness SLA — the component just renders the verdict.
 */
export function DataFreshnessBadge({
  at,
  tone,
  now = new Date(),
}: DataFreshnessBadgeProps) {
  const ago = relativeTime(at, now);
  const aria = `Data ${TONE_LABEL[tone]}, updated ${ago}`;
  const t = TONE[tone];
  return (
    <span
      aria-label={aria}
      className={`inline-flex items-center gap-sp-1 rounded-r-chip px-sp-2 py-sp-1 font-mono text-fs-meta ${t.bg} ${t.fg}`}
      data-tone={tone}
    >
      <span>updated {ago}</span>
    </span>
  );
}
